/* ==========================================================================
   Specsy — SPECGT shopping companion

   Working name only. Every customer-facing string that names the assistant
   comes from section settings, so the name can change without touching code.

   Deliberately NOT a language model: there is no generative layer, so there
   is nothing that can invent a product, price, variant, policy or promise.
   Product facts come from this shop's own public /products.json. Cart facts
   come from Shopify's own public cart endpoints. Policy answers are fixed
   merchant-authored strings. Anything else is declined.

   No credentials. No Admin API. No payment data, ever.

   What it can genuinely do:
     discover -> understand -> recommend -> explain -> save
              -> choose a real variant -> add to the real Shopify cart
              -> cross-sell -> attach the store's existing affiliate code
              -> hand off to Shopify's own checkout

   Routing priority, highest first:
     1  pending follow-up state          7  price / filter
     2  cart + buying intents            8  product / category discovery
     3  current PDP product              9  collection discovery
     4  active or recommended product   10  gift discovery
     5  policy                          11  keyword retrieval
     6  refinement of current results   12  safe fallback

   Journey discipline: a NEW top-level journey clears transient discovery
   filters; a REFINEMENT keeps them. Explicitly saved products survive both.
   ========================================================================== */

(function () {
  'use strict';

  var ROOT_ID = 'specgt-shopping-assistant';
  var CACHE_KEY = 'specgt-sa-index-v3';
  var SAVED_KEY = 'specgt-sa-saved-v1';
  var AFFILIATE_LS = 'shopify_affiliate_code'; /* the key the cart page already uses */

  /* Conversation persistence. localStorage, not sessionStorage, because a
     sessionStorage conversation dies on a new tab and is not shared between
     tabs — which is exactly the failure being fixed. Versioned so a future
     shape change can be detected and discarded rather than mis-read. */
  var CONVO_KEY = 'specgt-sa-convo-v1';
  var CONVO_V = 1;
  var SYNC_CHANNEL = 'specgt-sa-sync';
  var MAX_ENTRIES = 400;   /* keeps a long session inside the storage quota */

  /* Fixed interface copy. Merchant-editable strings arrive in cfg.copy and are
     merged over these, so the theme editor stays small but nothing is hard to
     reword later. */
  var T = {
    welcome: 'Hey there! 👋 I’m Specsy, your SPECGT shopping companion. Looking for something specific or just exploring? I’m here to help!',
    welcomePdp: 'Hey! 👋 I’m Specsy. Having a look at this one? I can check sizes and colours, find you something similar, or get it into your cart.',
    welcomeCart: 'Hey! 👋 I’m Specsy. Want a hand before you check out? I can run through your cart, suggest something that goes with it, or help you finish up.',
    welcomeBack: 'Good to see you again — picking up where we left off.',

    chipDiscover: 'Find something for me',
    chipTee: 'Shop T-shirts',
    chipGift: 'Find a gift',
    chipBudget: 'Shop by budget',
    chipOrder: 'Help me order',
    chipCollections: 'Browse collections',

    discoverPrompt: 'Happy to help. What sort of thing are you after?',
    budgetPrompt: 'What are you looking to spend?',
    giftPrompt: 'Nice. Who is it for?',
    giftCulture: 'Into car culture',
    giftRacing: 'Into racing',
    giftGeneral: 'Something general',

    culture: 'Car culture',
    racing: 'Racing',

    showMore: 'Show me more',
    cheaper: 'Something cheaper',
    similar: 'Similar style',
    pairs: 'What goes with it',
    startOver: 'Start over',
    dropPrice: 'Drop the price limit',

    viewProduct: 'View product',
    save: 'Save',
    saved: 'Saved',
    unsave: 'Remove from picks',
    addToCart: 'Add to cart',
    seeSizes: 'Sizes and colours',
    sizeGuide: 'View size guide',

    myPicks: 'My picks',
    noPicks: 'You have not saved anything yet. Tap Save on any product and it will land here.',
    picksIntro: 'Here is what you have saved:',
    copyPicks: 'Copy my picks',
    copied: 'Copied — paste it wherever you like.',
    copyFailed: 'Your browser blocked copying, but every product above is still a link.',
    emailPicks: 'Email my picks',
    emailUnavailable: 'Emailing picks is not switched on yet.',
    savedOne: 'Saved to your picks.',
    removedOne: 'Removed from your picks.',

    viewCart: 'View cart',
    checkout: 'Checkout',
    keepShopping: 'Keep shopping',
    goWithIt: 'Find something to go with it',
    cartEmpty: 'Your cart is empty at the moment. I can help you find something first.',
    cartIntro: 'Here is what is in your cart:',
    cartTotal: 'Total: {price}',
    cartUnavailable: 'I could not read your cart just now. You can still open the cart page directly.',
    remove: 'Remove',
    removed: 'Removed from your cart.',
    changeFailed: 'I could not change that line. Nothing was altered.',

    whichOption: 'Which {option}?',
    noCombo: 'I cannot find that combination in stock. Want to try another option?',
    confirmAdd: 'Ready to add: {title} — {variant} — {price}.',
    addFailed: 'That did not go through, so nothing was added to your cart.',
    retry: 'Try again',
    added: 'Added to your cart: {title} — {variant}.',
    addedSimple: 'Added to your cart: {title}.',
    crossSell: 'Want something to go with it?',
    yesPlease: 'Yes, show me',
    soldOut: 'That one is not available to order right now.',

    /* A referral code identifies who referred the shopper and is saved with
       the order. It is NOT a discount, and these strings say so plainly,
       because "code" next to a checkout button otherwise reads as money off.
       They also stop short of promising the referrer anything: the store's
       attribution process has not been verified, so the assistant states
       only what it can see — that the code is recorded on the order. */
    affiliateAsk: 'Were you referred by one of our partners? I can add their referral code to your order. It is not a discount code, so your total stays the same.',
    affiliateEnter: 'Enter code',
    affiliateSkip: 'Skip',
    affiliatePrompt: 'Go ahead and type their referral code. It is saved with your order to record who referred you, and it does not change your total.',
    affiliateSet: 'Referral code {code} is saved with your order. It records who referred you and does not change your total.',
    affiliateHas: 'Referral code {code} is already saved with this order. It does not change your total.',
    affiliateReplace: 'Use a different code',
    affiliateFailed: 'I could not save that code, so nothing was changed. You can also enter it on the cart page.',
    affiliateNone: 'I cannot add a referral code from here.',
    affiliateAdd: 'Add a referral code',

    checkoutReady: 'You have {n} in your cart, {price} in total. Payment happens on Shopify\'s secure checkout — I will take you straight there.',
    goCheckout: 'Proceed to checkout',

    /* Conversation controls */
    menu: 'Conversation options',
    newConvo: 'New conversation',
    newConvoDone: 'Fresh start. What are you after?',
    clearConvo: 'Clear conversation',
    clearAsk: 'Clear this conversation? I will not be able to bring it back, and your saved picks stay where they are.',
    clearYes: 'Yes, clear it',
    clearNo: 'Keep it',
    cleared: 'Cleared. Say the word whenever you want to start looking.',
    storageOff: 'Your browser is blocking storage, so this conversation will not survive a refresh. Everything else still works.',

    /* Transcript */
    transcript: 'Request chat transcript',
    download: 'Download transcript',
    transcriptEmpty: 'There is nothing to send yet — we have not said much. Ask me something first and it will all be in there.',
    downloaded: 'Saved. Your transcript is in your downloads.',
    downloadFailed: 'Your browser blocked the download. You can still select the conversation above and copy it.',
    transcriptUnavailable: 'I cannot email transcripts yet — the store has no email service connected to me, and I am not going to take your address for something I cannot deliver. I can save a copy to your device right now instead.',
    transcriptAsk: 'Happy to send you a copy of this chat. What email address should it go to?',
    transcriptBadEmail: 'That does not look like an email address. Want to try again?',
    transcriptConfirm: 'I will send this conversation to {email}. It is just the messages between us — nothing about your cart, your payment details or anywhere else you have browsed. Send it?',
    transcriptSend: 'Send it',
    transcriptCancel: 'Cancel',
    transcriptCancelled: 'No problem, nothing sent.',
    transcriptSent: 'Sent. Check {email} in a few minutes, and look in spam if it is hiding.',
    transcriptFailed: 'That did not go through, so nothing was sent. You can download a copy instead.',
    transcriptSlowDown: 'I have just sent one of those. Give it a minute before asking again.',

    /* Escalation.
       humanNote is deliberately NOT a merchant setting. It is a factual
       disclosure, not marketing copy: Shopify Inbox has no supported way to
       receive this conversation, so the shopper must be told that the summary
       travels only if they paste it. */
    human: 'This one needs a person, and SPECGT’s support team can help.',
    humanNote: 'One thing to know: they will not see this chat automatically. Tap Copy summary, then paste it into the live chat or the message form on the support page so they have the background.',
    contactUs: 'Contact the team',
    copySummary: 'Copy summary',
    summaryCopied: 'Copied. Open the support page, then paste it into the live chat or the message form and the team will have the context.',
    summaryCopyFailed: 'Your browser blocked copying. Download the transcript instead, or open the contact page — I will put the summary in the message box there for you to check.',
    contactPrefilled: 'I have put a short summary in the message box. Have a read, add anything else, and send it when you are happy.',

    whichOne: 'Which one do you mean?',
    noSubject: 'Tell me which product you mean and I will pull up the details.',
    sizingFallback: 'Sizes are listed on each product page. Tell me which product and I will read its options.',
    noPrice: 'I cannot confirm a price for that one.',
    none: 'Tell me a product type, a price, or a collection and I will pull up what fits.',
    noneFor: 'Nothing in the range matches “{q}”.',
    noMore: 'That is everything matching that.',
    noneCheaper: 'Nothing in that selection sits under {price}.',
    noneWith: 'Nothing in the range matches {what}.',
    noPair: 'Nothing in the range pairs obviously with that one.',
    matchesIntro: 'Here is what matches:',
    similarIntro: 'Along the same lines as “{title}”:',
    pairsIntro: 'These go well with “{title}”:',
    pairsWhy: 'Pairs well',
    similarWhy: 'Similar style',
    collectionsIntro: 'Here is what you can browse:',
    offline: 'I could not reach the product catalogue just now. Please try again in a moment.'
  };

  function boot() {
    var root = document.getElementById(ROOT_ID);
    if (!root || root.dataset.booted === 'true') return;
    root.dataset.booted = 'true';

    var cfg;
    try { cfg = JSON.parse(root.querySelector('[data-sa-config]').textContent); }
    catch (e) { return; }

    /* merchant overrides win */
    var copy = cfg.copy || {};
    for (var k in copy) { if (copy[k]) T[k] = copy[k]; }

    var launcher = root.querySelector('[data-sa-launcher]');
    var panel = root.querySelector('[data-sa-panel]');
    var closeBtn = root.querySelector('[data-sa-close]');
    var log = root.querySelector('[data-sa-log]');
    var form = root.querySelector('[data-sa-form]');
    var input = root.querySelector('[data-sa-input]');

    var catalogue = null;
    var loading = false;

    var base = cfg.rootUrl || '/';
    var URLS = {
      products: base + 'products.json?limit=250',
      cart: base + 'cart.js',
      add: base + 'cart/add.js',
      change: base + 'cart/change.js',
      update: base + 'cart/update.js',
      cartPage: base + 'cart',
      checkout: base + 'checkout'
    };

    /* ---- conversation state --------------------------------------------
       Bounded to this browsing session. `saved` is the only thing that
       survives a new journey, because the shopper put it there on purpose. */
    var S = {
      currentPageProduct: null, /* the PDP we are standing on, if any */
      activeProduct: null,      /* the product currently being discussed */
      results: [],              /* recommendation history — NOT saved picks */
      shownCount: 0,
      pending: null,            /* {kind:'gift'|'affiliate'|'pick'|'option', ...} */
      filters: { types: null, tag: null, ceiling: null, gift: false, q: '' },
      saved: [],                /* handles the shopper explicitly saved */
      buy: null                 /* {handle, chosen:{idx:value}} */
    };

    try {
      var raw = window.sessionStorage.getItem(SAVED_KEY);
      if (raw) S.saved = JSON.parse(raw) || [];
    } catch (e) { /* storage blocked — picks simply do not persist */ }

    function persistSaved() {
      try { window.sessionStorage.setItem(SAVED_KEY, JSON.stringify(S.saved)); } catch (e) {}
    }

    /* ---- conversation persistence ---------------------------------------
       The conversation is stored as an ordered list of replayable entries
       rather than as HTML, so a restore rebuilds real nodes with working
       chips instead of pasting markup back in.

       Every entry carries a stable id of "<conversationId>:<seq>". Saves
       MERGE on that id rather than overwriting, so two tabs writing at once
       cannot drop each other's messages, and a replay can never duplicate an
       entry it already holds.

       localStorage, not sessionStorage: a sessionStorage conversation dies in
       a new tab and is never shared between tabs, which is the whole problem.

       Nothing sensitive goes in here. Messages only — no payment details, no
       cart contents beyond what was said out loud, no browsing history. */

    var prefilled = false;   /* set when an escalation link filled the contact form */
    var storageOK = true;
    var replaying = false;       /* true while rebuilding, so replay does not re-record */
    var convo = null;            /* the in-memory record */

    function newId() {
      return 'c' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    }

    function blankConvo() {
      return { v: CONVO_V, id: newId(), startedAt: Date.now(), updatedAt: Date.now(), seq: 0, entries: [], state: {} };
    }

    function readConvo() {
      try {
        var raw = window.localStorage.getItem(CONVO_KEY);
        if (!raw) return null;
        var rec = JSON.parse(raw);
        if (!rec || rec.v !== CONVO_V || !rec.entries || !rec.entries.length) return null;
        return rec;
      } catch (e) { storageOK = false; return null; }
    }

    /* Merge-on-write. Read what is there now, union by entry id, keep order. */
    function writeConvo() {
      if (!convo || !convo.entries.length) return;   /* never overwrite a saved
                                                        conversation with an empty one */
      try {
        var stored = null;
        try { stored = JSON.parse(window.localStorage.getItem(CONVO_KEY) || 'null'); } catch (e) { stored = null; }

        var out = convo;
        if (stored && stored.v === CONVO_V && stored.id === convo.id && stored.entries) {
          var seen = {};
          var merged = [];
          convo.entries.concat(stored.entries).forEach(function (e) {
            if (e && e.id && !seen[e.id]) { seen[e.id] = 1; merged.push(e); }
          });
          merged.sort(function (a, b) { return (a.n || 0) - (b.n || 0); });
          out = {
            v: CONVO_V, id: convo.id,
            startedAt: Math.min(convo.startedAt, stored.startedAt || convo.startedAt),
            updatedAt: Date.now(),
            seq: Math.max(convo.seq, stored.seq || 0),
            entries: merged.slice(-MAX_ENTRIES),
            state: convo.state
          };
          convo.entries = out.entries;
          convo.seq = out.seq;
        } else {
          out.entries = out.entries.slice(-MAX_ENTRIES);
          out.updatedAt = Date.now();
        }

        window.localStorage.setItem(CONVO_KEY, JSON.stringify(out));
        broadcast();
      } catch (e) {
        /* quota or a blocked store — the conversation still works in memory */
        storageOK = false;
      }
    }

    function record(entry) {
      if (replaying || !convo) return;
      convo.seq += 1;
      entry.id = convo.id + ':' + convo.seq;
      entry.n = convo.seq;
      entry.ts = entry.ts || Date.now();
      convo.entries.push(entry);
      convo.state = { filters: S.filters, activeProduct: S.activeProduct };
      writeConvo();
    }

    /* ---- cross-tab sync --------------------------------------------------
       BroadcastChannel where available, the storage event as the fallback.
       Both carry a notification only; the receiving tab re-reads storage, so
       there is one source of truth and no message travels twice. */

    var channel = null;
    try {
      if (window.BroadcastChannel) channel = new window.BroadcastChannel(SYNC_CHANNEL);
    } catch (e) { channel = null; }

    function broadcast() {
      if (!channel || !convo) return;
      try { channel.postMessage({ id: convo.id, updatedAt: convo.updatedAt || Date.now() }); } catch (e) {}
    }

    function adoptFromStorage() {
      var rec = readConvo();
      if (!rec) return;
      /* Another tab started a different conversation, or added to this one. */
      if (convo && rec.id === convo.id && rec.entries.length <= convo.entries.length) return;
      convo = rec;
      if (root.getAttribute('data-open') === 'true') replay();
    }

    if (channel) {
      channel.onmessage = function () { adoptFromStorage(); };
    }
    window.addEventListener('storage', function (e) {
      if (e && e.key && e.key !== CONVO_KEY) return;
      adoptFromStorage();
    });

    /* A new top-level journey drops transient discovery context. Saved picks
       and the PDP we are standing on are deliberately untouched. */
    function startJourney() {
      S.results = [];
      S.shownCount = 0;
      S.pending = null;
      S.filters = { types: null, tag: null, ceiling: null, gift: false, q: '' };
      S.activeProduct = null;
      S.buy = null;
    }

    /* ---- money ---------------------------------------------------------- */

    function money(amount) {
      var n = parseFloat(amount);
      if (isNaN(n)) return '';
      var fmt = cfg.moneyFormat || '${{amount}}';
      var d = n.toFixed(2);
      return fmt
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(n)))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, d.replace('.', ','))
        .replace(/\{\{\s*amount\s*\}\}/g, d);
    }

    function cents(c) { return money((parseInt(c, 10) || 0) / 100); }

    function priceLabel(p) {
      if (!p.price || !isFinite(p.price.min) || !isFinite(p.price.max)) return '';
      if (p.price.min === p.price.max) return money(p.price.min);
      return money(p.price.min) + ' – ' + money(p.price.max);
    }

    function fill(s, map) {
      return String(s).replace(/\{(\w+)\}/g, function (m, key) {
        return map[key] != null ? map[key] : m;
      });
    }

    /* Shopify's cart endpoints return HTML-escaped text, so a variant of
       "Red / S" can arrive as "Red / &#x53;". Everything here is rendered
       with textContent, which prints an entity literally rather than
       resolving it, so cart-sourced strings are decoded on the way in.

       This is safe precisely BECAUSE the destination stays textContent: a
       decoded "<" is printed as a character and never parsed as markup.
       It must not be "fixed" with innerHTML or a detached-textarea trick,
       which would turn a display bug into an injection path.

       Catalogue strings come from /products.json, which is not escaped, so
       they are left alone. */
    var NAMED = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };

    function decodeEntities(s) {
      if (s == null) return '';
      return String(s).replace(/&(#x?[0-9a-fA-F]+|amp|lt|gt|quot|apos|nbsp);/g, function (m, g) {
        if (g.charAt(0) === '#') {
          var n = /^#x/i.test(g) ? parseInt(g.slice(2), 16) : parseInt(g.slice(1), 10);
          return (isFinite(n) && n > 0 && n <= 0xFFFF) ? String.fromCharCode(n) : m;
        }
        return NAMED[g] || m;
      });
    }

    /* A cart line's display strings, decoded once at the boundary. */
    function lineTitle(it) { return decodeEntities(it.product_title || it.title); }

    function lineVariant(it) {
      /* Decoded BEFORE the comparison: an escaped "Default Title" would
         otherwise slip past this check and be shown to the shopper. */
      var v = decodeEntities(it.variant_title);
      return v && v !== 'Default Title' ? v : '';
    }

    /* ---- dom ------------------------------------------------------------ */

    function el(tag, cls, text) {
      var n = document.createElement(tag);
      if (cls) n.className = cls;
      if (text != null) n.textContent = text;
      return n;
    }

    function scrollDown() { log.scrollTop = log.scrollHeight; }

    /* Node removal rather than innerHTML, so this file never assigns markup. */
    function clearLog() {
      while (log.firstChild) log.removeChild(log.firstChild);
    }

    function avatarNode(cls) {
      var span = el('span', cls);
      span.setAttribute('aria-hidden', 'true');
      span.innerHTML = cfg.avatarSvg || '';
      return span;
    }

    function say(text, who) {
      var row = el('div', 'specgt-sa__row specgt-sa__row--' + (who || 'bot'));
      if (who !== 'user') row.appendChild(avatarNode('specgt-sa__rowAvatar'));
      var m = el('div', 'specgt-sa__msg specgt-sa__msg--' + (who || 'bot'));
      String(text).split('\n').forEach(function (line) { m.appendChild(el('p', null, line)); });
      row.appendChild(m);
      log.appendChild(row);
      scrollDown();
      record({ k: 'msg', who: who === 'user' ? 'user' : 'bot', text: String(text) });
    }

    function setState(state) { root.setAttribute('data-avatar', state || 'idle'); }

    /* Chips carry a structured action. Nothing is ever resolved by re-reading
       a visible label, which is what made the old gift flow loop. A chip with
       `href` is a real link rather than an action. */
    function chips(items) {
      if (!items || !items.length) return;
      var wrap = el('div', 'specgt-sa__chips');
      items.forEach(function (it) {
        if (!it) return;
        var b;
        if (it.href) {
          b = el('a', 'specgt-sa__chip specgt-sa__chip--link', it.label);
          b.href = it.href;
          if (it.blank) { b.target = '_blank'; b.rel = 'noopener'; }
        } else {
          b = el('button', 'specgt-sa__chip', it.label);
          b.type = 'button';
          b.addEventListener('click', function () {
            say(it.label, 'user');
            dispatch(it.action);
          });
        }
        if (it.primary) b.className += ' specgt-sa__chip--primary';
        wrap.appendChild(b);
      });
      log.appendChild(wrap);
      scrollDown();
      /* Chip actions are plain data, so a restored chip is a working chip
         rather than a dead label. */
      record({ k: 'chips', items: items.filter(Boolean).map(function (it) {
        return { label: it.label, action: it.action, href: it.href, blank: it.blank, primary: it.primary };
      }) });
    }

    /* ---- product cards --------------------------------------------------
       The image leads and is large enough to actually judge the product.

       Image handling is deliberately conservative, because a previous build
       rendered correct-looking markup that shoppers still saw as blank:
         - one plain `src`, no srcset and no `sizes`, so there is no
           candidate-selection step that can resolve to nothing;
         - `loading="eager"`, because a lazy image inserted into a scrolling
           container that has just finished an opacity/visibility transition
           is exactly the case where deferred loading can never trigger;
         - the media box paints its own background and holds its own size, so
           a card can never collapse to zero height while loading;
         - an explicit error handler swaps in a placeholder, so a failed load
           shows an intentional tile rather than a broken-image icon. */

    var saveButtons = {};

    function placeholder() {
      var span = el('span', 'specgt-sa__img specgt-sa__img--empty');
      span.setAttribute('aria-hidden', 'true');
      return span;
    }

    function thumb(url, w) {
      if (!url) return '';
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
    }

    function mediaNode(src, alt) {
      var media = el('div', 'specgt-sa__media');
      if (!src) { media.appendChild(placeholder()); return media; }
      var img = document.createElement('img');
      img.className = 'specgt-sa__img';
      img.src = thumb(src, 280);
      img.alt = alt || '';
      img.setAttribute('width', '120');
      img.setAttribute('height', '120');
      img.setAttribute('loading', 'eager');
      img.setAttribute('decoding', 'async');
      img.addEventListener('error', function () {
        if (img.parentNode === media) { media.removeChild(img); media.appendChild(placeholder()); }
      });
      img.addEventListener('load', function () { media.setAttribute('data-loaded', 'true'); });
      media.appendChild(img);
      return media;
    }

    function isSaved(h) { return S.saved.indexOf(h) !== -1; }

    function refreshSaveButtons(h) {
      var list = saveButtons[h] || [];
      var on = isSaved(h);
      list.forEach(function (b) {
        b.textContent = on ? T.saved : T.save;
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        b.setAttribute('data-saved', on ? 'true' : 'false');
      });
    }

    function toggleSave(h) {
      var i = S.saved.indexOf(h);
      if (i === -1) { S.saved.push(h); say(T.savedOne); }
      else { S.saved.splice(i, 1); say(T.removedOne); }
      persistSaved();
      refreshSaveButtons(h);
    }

    function card(p, reason) {
      var wrap = el('div', 'specgt-sa__card');
      wrap.appendChild(mediaNode(p.image, p.title));

      var body = el('div', 'specgt-sa__body');

      var name = el('a', 'specgt-sa__name', p.title);
      name.href = p.url;
      body.appendChild(name);

      var label = priceLabel(p);
      if (label) body.appendChild(el('span', 'specgt-sa__price', label));
      if (reason) body.appendChild(el('span', 'specgt-sa__why', reason));

      var actions = el('div', 'specgt-sa__cardActions');

      if (p.available) {
        var buy = el('button', 'specgt-sa__cardBtn specgt-sa__cardBtn--primary', T.addToCart);
        buy.type = 'button';
        buy.addEventListener('click', function () {
          say(T.addToCart + ': ' + p.title, 'user');
          dispatch({ kind: 'buy', handle: p.handle });
        });
        actions.appendChild(buy);
      }

      var sv = el('button', 'specgt-sa__cardBtn specgt-sa__cardBtn--save', isSaved(p.handle) ? T.saved : T.save);
      sv.type = 'button';
      sv.setAttribute('aria-pressed', isSaved(p.handle) ? 'true' : 'false');
      sv.setAttribute('data-saved', isSaved(p.handle) ? 'true' : 'false');
      sv.addEventListener('click', function () { toggleSave(p.handle); });
      (saveButtons[p.handle] = saveButtons[p.handle] || []).push(sv);
      actions.appendChild(sv);

      var view = el('a', 'specgt-sa__cta', T.viewProduct);
      view.href = p.url;
      actions.appendChild(view);

      body.appendChild(actions);
      wrap.appendChild(body);
      return wrap;
    }

    function renderCards(list, reasons) {
      var wrap = el('div', 'specgt-sa__cards');
      list.forEach(function (p, i) { wrap.appendChild(card(p, reasons ? reasons[i] : null)); });
      log.appendChild(wrap);
      scrollDown();
      /* Only handles are stored; prices and availability are re-read from the
         catalogue on replay so a restored card can never show a stale price. */
      record({
        k: 'cards',
        handles: list.map(function (p) { return p.handle; }),
        reasons: list.map(function (p, i) { return (reasons && reasons[i]) || ''; })
      });
    }

    /* ---- catalogue ------------------------------------------------------ */

    function build(rawList) {
      return (rawList || []).map(function (p) {
        var variants = p.variants || [];
        var prices = variants.map(function (v) { return parseFloat(v.price); })
                             .filter(function (n) { return !isNaN(n); });
        var range = prices.length ? { min: Math.min.apply(null, prices), max: Math.max.apply(null, prices) } : null;
        var options = (p.options || []).map(function (o) {
          return { name: String(o.name || ''), values: (o.values || []).slice() };
        });
        var byName = function (n) {
          var o = options.filter(function (x) { return x.name.toLowerCase() === n; })[0];
          return o ? o.values : [];
        };
        return {
          title: p.title,
          handle: p.handle,
          url: base + 'products/' + p.handle,
          type: p.product_type || '',
          tags: (p.tags || []).map(function (t) { return String(t).toLowerCase(); }),
          text: String(p.body_html || '').replace(/<[^>]*>/g, ' ').toLowerCase().slice(0, 900),
          image: (p.images && p.images[0]) ? p.images[0].src : '',
          price: range,
          options: options,
          variants: variants.map(function (v) {
            return {
              id: v.id,
              title: v.title,
              opts: [v.option1, v.option2, v.option3],
              price: parseFloat(v.price),
              available: !!v.available
            };
          }),
          sizes: byName('size'),
          colours: byName('color').concat(byName('colour')),
          available: variants.some(function (v) { return v.available; })
        };
      });
    }

    function load() {
      if (catalogue) return Promise.resolve(catalogue);
      if (loading) return loading;

      try {
        var cached = window.sessionStorage.getItem(CACHE_KEY);
        if (cached) {
          catalogue = JSON.parse(cached);
          if (catalogue && catalogue.length) return Promise.resolve(catalogue);
          catalogue = null;
        }
      } catch (e) { /* storage blocked — fetch instead */ }

      setState('thinking');
      loading = fetch(URLS.products, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.json(); })
        .then(function (j) {
          catalogue = build(j.products || []);
          try { window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(catalogue)); } catch (e) {}
          return catalogue;
        })
        .catch(function () { catalogue = null; loading = false; return null; });
      return loading;
    }

    function byHandle(h) {
      if (!h || !catalogue) return null;
      return catalogue.filter(function (p) { return p.handle === h; })[0] || null;
    }

    /* ---- scoring --------------------------------------------------------
       Transparent and explainable. Reasons are written in shopper language,
       never in the internal vocabulary of the matcher. */

    var TYPES = [
      { re: /\b(t[- ]?shirts?|tees?|shirts?|tanks?)\b/, types: ['t-shirt'], label: 'T-shirts' },
      { re: /\b(hats?|caps?|headwear|snapbacks?|truckers?|dad cap|beanies?)\b/, types: ['hats'], label: 'hats' },
      { re: /\b(mugs?|tumblers?|drinkware|cups?)\b/, types: ['mug'], label: 'drinkware' },
      { re: /\b(phone ?cases?|cases?)\b/, types: ['phone case'], label: 'phone cases' },
      { re: /\b(pets?|dogs?|bandanas?)\b/, types: ['pets'], label: 'pet gear' },
      { re: /\b(mouse ?pads?|desk ?mats?)\b/, types: ['home decor'], label: 'desk gear' },
      { re: /\b(accessor\w*|coolers?|koozies?)\b/, types: ['accessories'], label: 'accessories' }
    ];

    var TYPE_LABELS = {
      't-shirt': 'T-shirts', 'hats': 'hats', 'mug': 'drinkware',
      'phone case': 'phone cases', 'pets': 'pet gear',
      'home decor': 'desk gear', 'accessories': 'accessories'
    };

    var STOP = ['show','me','a','an','the','i','want','need','find','for','some','something','with','you','have','do','get','looking','look','to','my','is','it','of','and','or','please','can','that','this','are','what'];

    /* Price phrasing, stripped once the ceiling has been read out of it, so
       "t-shirts under 30" does not go on to search for the word "under". */
    var PRICE_WORDS = ['under','below','less','than','max','maximum','cheaper','cheap','budget','around','about','over','upto','only'];

    function words(q) {
      return q.toLowerCase().replace(/[^a-z0-9$ ]/g, ' ').split(/\s+/)
        .filter(function (w) { return w.length > 2 && STOP.indexOf(w) === -1; });
    }

    /* Shopper-facing wording for why something surfaced. */
    function tagReason(tag) {
      var t = String(tag).toLowerCase();
      if (t.indexOf('car culture') !== -1) return 'Car culture pick';
      if (t.indexOf('racing') !== -1) return 'Racing-inspired';
      if (t.indexOf('jdm') !== -1) return 'JDM pick';
      if (t.indexOf('drift') !== -1) return 'Drift pick';
      return tag.charAt(0).toUpperCase() + tag.slice(1) + ' pick';
    }

    function score(p, ws, f) {
      var s = 0, why = [];
      if (f.types && f.types.indexOf(p.type.toLowerCase()) !== -1) s += 10;
      if (f.tag) {
        if (p.tags.some(function (t) { return t.indexOf(f.tag) !== -1; })) { s += 12; why.push(tagReason(f.tag)); }
        else if (p.text.indexOf(f.tag) !== -1 || p.title.toLowerCase().indexOf(f.tag) !== -1) { s += 5; why.push(tagReason(f.tag)); }
      }
      ws.forEach(function (w) {
        if (p.title.toLowerCase().indexOf(w) !== -1) s += 6;
        if (p.tags.some(function (t) { return t.indexOf(w) !== -1; })) s += 4;
        if (p.type.toLowerCase().indexOf(w) !== -1) s += 3;
        if (p.text.indexOf(w) !== -1) s += 1;
      });
      if (f.ceiling != null && p.price && p.price.min <= f.ceiling) { s += 4; why.push('Under ' + money(f.ceiling)); }
      return { s: s, why: why };
    }

    function query(q, f) {
      f = f || {};
      var ws = q ? words(q) : [];
      var pool = catalogue.slice();

      if (f.types) pool = pool.filter(function (p) { return f.types.indexOf(p.type.toLowerCase()) !== -1; });
      if (f.ceiling != null) pool = pool.filter(function (p) { return p.price && p.price.min <= f.ceiling; });
      if (f.tag) {
        pool = pool.filter(function (p) {
          return p.tags.some(function (t) { return t.indexOf(f.tag) !== -1; }) ||
                 p.text.indexOf(f.tag) !== -1 || p.title.toLowerCase().indexOf(f.tag) !== -1;
        });
      }
      if (f.exclude) pool = pool.filter(function (p) { return p.handle !== f.exclude; });

      var scored = pool.map(function (p) {
        var r = score(p, ws, f);
        return { p: p, s: r.s, why: r.why };
      });
      if (ws.length) scored = scored.filter(function (x) { return x.s > 0; });

      scored.sort(function (a, b) {
        if (b.s !== a.s) return b.s - a.s;
        var x = a.p.price ? a.p.price.min : Infinity;
        var y = b.p.price ? b.p.price.min : Infinity;
        return x - y;
      });
      return scored;
    }

    function lowest() {
      var n = catalogue.reduce(function (m, p) { return p.price ? Math.min(m, p.price.min) : m; }, Infinity);
      return isFinite(n) ? n : null;
    }

    /* Natural headline for the current filter set, e.g.
       "Car culture gift ideas under $30:" — never "Here are car culture:". */
    function headline() {
      var parts = [];
      if (S.filters.tag) {
        var t = S.filters.tag;
        parts.push(t.indexOf('car culture') !== -1 ? 'car culture'
                 : (t.indexOf('racing') !== -1 ? 'racing' : t));
      }
      if (S.filters.types) {
        parts.push(TYPE_LABELS[S.filters.types[0]] || S.filters.types[0]);
      } else {
        parts.push(S.filters.gift ? 'gift ideas' : 'picks');
      }
      var s = parts.join(' ');
      if (S.filters.ceiling != null) s += ' under ' + money(S.filters.ceiling);
      return s.charAt(0).toUpperCase() + s.slice(1) + ':';
    }

    function describeFilters() {
      var bits = [];
      if (S.filters.tag) bits.push(S.filters.tag);
      if (S.filters.types) bits.push(TYPE_LABELS[S.filters.types[0]] || S.filters.types[0]);
      if (S.filters.ceiling != null) bits.push('under ' + money(S.filters.ceiling));
      return bits.join(' ');
    }

    /* ---- presenting ----------------------------------------------------- */

    function present(scored, intro, empty) {
      S.results = scored;
      S.shownCount = 0;
      if (!scored.length) {
        setState('none');
        say(empty || T.none);
        chips(relaxChips());
        return;
      }
      setState('found');
      say(intro);
      /* A single result becomes the active product, so "this" has something
         honest to resolve to without ever guessing between candidates. */
      if (scored.length === 1) S.activeProduct = scored[0].p.handle;
      showMore();
    }

    function showMore() {
      var slice = S.results.slice(S.shownCount, S.shownCount + 3);
      if (!slice.length) { say(T.noMore); chips(baseChips()); return; }
      renderCards(slice.map(function (x) { return x.p; }),
                  slice.map(function (x) { return x.why.length ? x.why.slice(0, 2).join(' · ') : ''; }));
      S.shownCount += slice.length;

      var c = [];
      if (S.shownCount < S.results.length) c.push({ label: T.showMore, action: { kind: 'more' } });
      c.push({ label: T.cheaper, action: { kind: 'cheaper' } });
      if (slice.length === 1) c.push({ label: T.pairs, action: { kind: 'pairs', handle: slice[0].p.handle } });
      c.push({ label: T.chipOrder, action: { kind: 'order' } });
      if (S.saved.length) c.push({ label: T.myPicks, action: { kind: 'picks' } });
      chips(c);
    }

    function baseChips() {
      var c = [
        { label: T.chipDiscover, action: { kind: 'discover' } },
        { label: T.chipTee, action: { kind: 'type', types: ['t-shirt'] } },
        { label: T.chipGift, action: { kind: 'gift' } },
        { label: T.chipBudget, action: { kind: 'budget' } },
        { label: T.chipOrder, action: { kind: 'order' } }
      ];
      if (S.saved.length) c.push({ label: T.myPicks, action: { kind: 'picks' } });
      return c;
    }

    function relaxChips() {
      var c = [];
      if (S.filters.ceiling != null) c.push({ label: T.dropPrice, action: { kind: 'relax', what: 'ceiling' } });
      if (S.filters.tag || S.filters.types) c.push({ label: T.startOver, action: { kind: 'reset' } });
      return c.concat(baseChips());
    }

    function runFilters(intro, empty) {
      var scored = query(S.filters.q || '', { types: S.filters.types, tag: S.filters.tag, ceiling: S.filters.ceiling });
      if (!scored.length) {
        var desc = describeFilters();
        setState('none');
        say(empty || (desc ? fill(T.noneWith, { what: desc }) : T.none));
        S.results = [];
        S.shownCount = 0;
        chips(relaxChips());
        return;
      }
      present(scored, intro || headline(), T.none);
    }

    /* ---- subject resolution --------------------------------------------- */

    function subject() {
      return byHandle(S.activeProduct) || byHandle(S.currentPageProduct) ||
             (S.results.length === 1 ? S.results[0].p : null);
    }

    function askWhich(then) {
      S.pending = { kind: 'pick', then: then };
      say(T.whichOne);
      chips(S.results.slice(0, Math.min(4, S.shownCount || 3)).map(function (x) {
        return {
          label: x.p.title.length > 32 ? x.p.title.slice(0, 31) + '…' : x.p.title,
          action: { kind: 'pick', handle: x.p.handle, then: then }
        };
      }));
    }

    /* ---- answers about one product -------------------------------------- */

    function productChips(p) {
      var c = [];
      if (p.available) c.push({ label: T.addToCart, primary: true, action: { kind: 'buy', handle: p.handle } });
      if (p.sizes.length > 1 && cfg.sizeGuideUrl && p.type.toLowerCase() === 't-shirt') {
        c.push({ label: T.sizeGuide, href: cfg.sizeGuideUrl, blank: true });
      }
      c.push({ label: T.similar, action: { kind: 'similar', handle: p.handle } });
      c.push({ label: T.pairs, action: { kind: 'pairs', handle: p.handle } });
      c.push({ label: isSaved(p.handle) ? T.unsave : T.save, action: { kind: 'save', handle: p.handle } });
      return c;
    }

    function detail(p, which) {
      S.activeProduct = p.handle;
      setState('found');

      if (which === 'sizes') {
        if (p.sizes.length > 1) say('“' + p.title + '” comes in ' + p.sizes.join(', ') + '.');
        else say('“' + p.title + '” comes in one size' + (p.sizes.length ? ' — ' + p.sizes[0] : '') + '.');
      } else if (which === 'colours') {
        say(p.colours.length
          ? '“' + p.title + '” comes in ' + p.colours.join(', ') + '.'
          : '“' + p.title + '” does not list colour options.');
      } else if (which === 'price') {
        var l = priceLabel(p);
        say(l ? '“' + p.title + '” is ' + l + '.' : T.noPrice);
      } else if (which === 'available') {
        say(p.available ? '“' + p.title + '” is available to order.' : '“' + p.title + '” is not available right now.');
      } else {
        var l2 = priceLabel(p);
        var bits = [];
        if (p.sizes.length > 1) bits.push(p.sizes.length + ' sizes');
        if (p.colours.length > 1) bits.push(p.colours.length + ' colours');
        say('“' + p.title + '”' + (l2 ? ' — ' + l2 : '') + '.' + (bits.length ? '\n' + bits.join(' · ') + '.' : ''));
      }

      renderCards([p]);
      chips(productChips(p));
    }

    /* ---- similar and complementary --------------------------------------
       Grounded in catalogue relationships only. Never "frequently bought
       together" — there is no purchase data here that could support it. */

    function similar(p) {
      S.activeProduct = p.handle;
      var tag = p.tags[0] || '';
      var scored = query('', { types: [p.type.toLowerCase()], tag: tag || null, exclude: p.handle });
      if (!scored.length) scored = query('', { types: [p.type.toLowerCase()], exclude: p.handle });
      scored.forEach(function (x) { x.why = [T.similarWhy]; });
      present(scored, fill(T.similarIntro, { title: p.title }), T.none);
    }

    var PAIRS = {
      't-shirt': ['hats', 'accessories', 'mug'],
      'hats': ['t-shirt', 'accessories'],
      'accessories': ['t-shirt', 'hats'],
      'mug': ['t-shirt', 'accessories'],
      'phone case': ['t-shirt', 'accessories'],
      'pets': ['t-shirt', 'accessories'],
      'home decor': ['t-shirt', 'mug']
    };

    function pairsWith(p, intro) {
      S.activeProduct = p.handle;
      var wants = PAIRS[p.type.toLowerCase()] || ['t-shirt'];
      var out = [];
      wants.forEach(function (t) {
        if (out.length >= 3) return;
        var tagged = query('', { types: [t], tag: p.tags[0] || null, exclude: p.handle });
        var any = tagged.length ? tagged : query('', { types: [t], exclude: p.handle });
        if (any.length) out.push(any[0]);
      });
      if (!out.length) { say(T.noPair); chips(baseChips()); return; }
      setState('found');
      say(intro || fill(T.pairsIntro, { title: p.title }));
      S.results = out;
      S.shownCount = out.length;
      renderCards(out.map(function (x) { return x.p; }), out.map(function () { return T.pairsWhy; }));
      chips(cartChips());
    }

    /* ---- saved picks -----------------------------------------------------
       Explicitly saved products only. Recommendations are not picks, and
       picks are not a cart. */

    function picks() {
      var items = S.saved.map(byHandle).filter(Boolean);
      if (!items.length) { setState('idle'); say(T.noPicks); chips(baseChips()); return; }

      setState('found');
      say(T.picksIntro);

      var box = el('div', 'specgt-sa__summary');
      items.forEach(function (p) {
        var r = el('div', 'specgt-sa__sumRow');
        var a = el('a', 'specgt-sa__sumLink', p.title);
        a.href = p.url;
        r.appendChild(a);
        var l = priceLabel(p);
        if (l) r.appendChild(el('span', 'specgt-sa__sumPrice', l));
        var rm = el('button', 'specgt-sa__sumRemove', T.remove);
        rm.type = 'button';
        rm.setAttribute('aria-label', T.unsave + ': ' + p.title);
        rm.addEventListener('click', function () { toggleSave(p.handle); picks(); });
        r.appendChild(rm);
        box.appendChild(r);
      });
      log.appendChild(box);

      var lines = items.map(function (p) {
        var l = priceLabel(p);
        return p.title + (l ? ' — ' + l : '') + '\n' + cfg.shopUrl + p.url.replace(/^\//, '');
      });

      var acts = [{ label: T.copyPicks, action: { kind: 'copy', text: lines.join('\n\n') } }];
      /* Email is offered only when a backend that can genuinely send it has
         been configured. Without one the button is absent rather than lying. */
      if (cfg.emailEnabled) acts.push({ label: T.emailPicks, action: { kind: 'email' } });
      acts.push({ label: T.chipOrder, action: { kind: 'order' } });
      chips(acts);
      scrollDown();
    }

    function copyPicks(text) {
      var done = function (ok) { say(ok ? T.copied : T.copyFailed); chips(baseChips()); };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(function () { done(true); }, function () { done(false); });
      } else { done(false); }
    }

    /* ---- replay ----------------------------------------------------------
       Rebuilds the log from stored entries. Product cards are re-rendered
       from the live catalogue by handle, so a restored conversation shows
       today's prices rather than whatever was true when it was said. */

    function staticNote(lines) {
      var box = el('div', 'specgt-sa__summary');
      lines.forEach(function (line) {
        var r = el('div', 'specgt-sa__sumRow');
        r.appendChild(el('span', 'specgt-sa__sumLink', line));
        box.appendChild(r);
      });
      log.appendChild(box);
    }

    function replay() {
      if (!convo || !convo.entries.length) return;
      replaying = true;
      clearLog();
      saveButtons = {};
      try {
        convo.entries.forEach(function (e) {
          if (e.k === 'msg') say(e.text, e.who);
          else if (e.k === 'chips') chips(e.items);
          else if (e.k === 'note') staticNote(e.lines || []);
          else if (e.k === 'cards') {
            var list = (e.handles || []).map(byHandle).filter(Boolean);
            if (list.length) {
              renderCards(list, (e.reasons || []).slice(0, list.length));
            } else if (e.handles && e.handles.length) {
              /* catalogue not loaded yet, or the product is gone */
              staticNote(e.handles.map(function (h) { return h.replace(/-/g, ' '); }));
            }
          }
        });
      } finally {
        replaying = false;
      }
      if (convo.state) {
        if (convo.state.filters) S.filters = convo.state.filters;
        if (convo.state.activeProduct) S.activeProduct = convo.state.activeProduct;
      }
      scrollDown();
    }

    /* ---- conversation controls ------------------------------------------ */

    function startConversation(announce) {
      convo = blankConvo();
      clearLog();
      saveButtons = {};
      startJourney();
      setState('idle');
      say(announce || opening());
      chips(openingChips());
      if (!storageOK) say(T.storageOff);
    }

    function clearConversation() {
      try { window.localStorage.removeItem(CONVO_KEY); } catch (e) {}
      convo = blankConvo();
      clearLog();
      saveButtons = {};
      startJourney();
      setState('idle');
      say(T.cleared);
      chips(openingChips());
      broadcast();
    }

    /* ---- transcript ------------------------------------------------------
       Built from the same stored entries, so what the shopper downloads is
       exactly what they saw. Product cards become a readable line each;
       chips are navigation, not conversation, so they are left out. */

    function transcriptText() {
      if (!convo || !convo.entries.length) return '';
      var name = cfg.assistantName || 'Specsy';
      var when = new Date(convo.startedAt || Date.now());
      var out = [];
      out.push('SPECGT — conversation with ' + name);
      out.push(cfg.shopUrl || '');
      out.push('Started: ' + when.toLocaleString());
      out.push('Conversation reference: ' + convo.id);
      out.push(new Array(60).join('-'));
      out.push('');

      convo.entries.forEach(function (e) {
        var t = new Date(e.ts || convo.startedAt);
        var stamp = ('0' + t.getHours()).slice(-2) + ':' + ('0' + t.getMinutes()).slice(-2);
        if (e.k === 'msg') {
          out.push('[' + stamp + '] ' + (e.who === 'user' ? 'You' : name) + ': ' + e.text.replace(/\n/g, '\n           '));
          out.push('');
        } else if (e.k === 'cards') {
          (e.handles || []).forEach(function (h, i) {
            var p = byHandle(h);
            var why = (e.reasons || [])[i];
            out.push('           • ' + (p ? p.title : h.replace(/-/g, ' ')) +
                     (p && priceLabel(p) ? ' — ' + priceLabel(p) : '') +
                     (why ? '  (' + why + ')' : ''));
            out.push('             ' + (cfg.shopUrl || '') + (p ? p.url : '/products/' + h).replace(/^\//, ''));
          });
          out.push('');
        } else if (e.k === 'note') {
          (e.lines || []).forEach(function (l) { out.push('           • ' + l); });
          out.push('');
        }
      });

      out.push(new Array(60).join('-'));
      out.push('Prices and availability are as shown at the time of writing.');
      return out.join('\n');
    }

    /* ---- concise summary for a human handoff -----------------------------
       Deliberately NOT the transcript. Support needs the gist, not a log, and
       a fragment has to stay short enough to survive a URL.

       It carries the last few exchanges and any products discussed. It carries
       no email address, no cart, no order data and no browsing history --
       whatever personal detail support needs, the shopper supplies in the form
       itself, where they can see what they are sending. */

    function summaryText(limit) {
      if (!convo || !convo.entries.length) return '';
      limit = limit || 1200;
      var name = cfg.assistantName || 'Specsy';

      var said = convo.entries.filter(function (e) { return e.k === 'msg'; }).slice(-6);
      var handles = [];
      convo.entries.forEach(function (e) {
        if (e.k !== 'cards') return;
        (e.handles || []).forEach(function (h) { if (handles.indexOf(h) === -1) handles.push(h); });
      });

      var out = ['SPECGT — summary of my chat with ' + name,
                 new Date(convo.startedAt || Date.now()).toLocaleString(), ''];

      said.forEach(function (e) {
        var t = String(e.text).replace(/\s+/g, ' ');
        if (t.length > 160) t = t.slice(0, 157) + '…';
        out.push((e.who === 'user' ? 'Me: ' : name + ': ') + t);
      });

      if (handles.length) {
        out.push('');
        out.push('Products we looked at:');
        handles.slice(0, 4).forEach(function (h) {
          var p = byHandle(h);
          out.push('- ' + (p ? p.title : h.replace(/-/g, ' ')) + (p ? '  ' + cfg.shopUrl + p.url.replace(/^\//, '') : ''));
        });
      }

      var text = out.join('\n');
      return text.length > limit ? text.slice(0, limit - 1) + '…' : text;
    }

    /* The summary rides in the URL FRAGMENT, not a query string, so it is
       never sent to the server and never reaches access logs, analytics or a
       referrer header. Kept short for the same reason. */
    function contactUrlWithSummary() {
      var base = cfg.supportUrl || '/pages/contact';
      var s = summaryText(700);
      if (!s) return base;
      return base + '#specsy=' + encodeURIComponent(s);
    }

    /* Prefills the message field ONLY, never submits, and never overwrites
       something the shopper has already typed. */
    function prefillContactForm() {
      var hash = window.location.hash || '';
      var m = hash.match(/[#&]specsy=([^&]+)/);
      if (!m) return;
      var body;
      try { body = decodeURIComponent(m[1]); } catch (e) { return; }
      if (!body) return;
      var field = document.querySelector('textarea[name="contact[body]"]');
      if (!field) return;
      if (field.value && field.value.trim()) return;
      field.value = body;
      prefilled = true;
    }

    function copySummary() {
      var text = summaryText(1200);
      if (!text) { say(T.transcriptEmpty); chips(baseChips()); return; }
      var after = function (ok) {
        setState(ok ? 'found' : 'none');
        say(ok ? T.summaryCopied : T.summaryCopyFailed);
        chips([{ label: T.contactUs, href: contactUrlWithSummary() },
               { label: T.download, action: { kind: 'download' } },
               { label: T.keepShopping, action: { kind: 'discover' } }]);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(function () { after(true); }, function () { after(false); });
      } else { after(false); }
    }

    function downloadTranscript() {
      var text = transcriptText();
      if (!text) { say(T.transcriptEmpty); chips(baseChips()); return; }
      try {
        var blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = 'specgt-chat-' + new Date().toISOString().slice(0, 10) + '.txt';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.setTimeout(function () { try { URL.revokeObjectURL(url); } catch (e) {} }, 1000);
        say(T.downloaded);
      } catch (e) {
        say(T.downloadFailed);
      }
      chips(baseChips());
    }

    /* Deliberately conservative: with no endpoint configured the assistant
       says plainly that it cannot email, and does NOT collect an address for
       something it cannot deliver. Setting the endpoint turns the whole flow
       on without any other change. */
    var lastTranscriptAt = 0;

    function requestTranscript() {
      if (!convo || !convo.entries.length) { say(T.transcriptEmpty); chips(baseChips()); return; }
      var endpoint = cfg.transcript && cfg.transcript.endpoint;
      if (!endpoint) {
        setState('none');
        say(T.transcriptUnavailable);
        chips([{ label: T.download, action: { kind: 'download' } },
               { label: T.keepShopping, action: { kind: 'discover' } }]);
        return;
      }
      S.pending = { kind: 'email' };
      setState('idle');
      say(T.transcriptAsk);
      chips([{ label: T.download, action: { kind: 'download' } },
             { label: T.transcriptCancel, action: { kind: 'transcriptCancel' } }]);
    }

    function validEmail(v) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v) && v.length <= 254;
    }

    function sendTranscript(email) {
      var endpoint = cfg.transcript && cfg.transcript.endpoint;
      if (!endpoint) { say(T.transcriptUnavailable); chips(baseChips()); return; }
      /* Light client-side throttle. Real abuse protection has to live on the
         endpoint; this only stops accidental repeats. */
      var now = Date.now();
      if (now - lastTranscriptAt < 60000) { say(T.transcriptSlowDown); chips(baseChips()); return; }

      setState('thinking');
      postJSON(endpoint, {
        email: email,
        shopDomain: cfg.shopUrl || '',
        locale: cfg.locale || '',
        conversationId: convo.id,
        startedAt: convo.startedAt,
        createdAt: now,
        messages: convo.entries.filter(function (e) { return e.k === 'msg'; })
                               .map(function (e) { return { role: e.who, text: e.text, ts: e.ts }; })
      }).then(function (res) {
        if (!res.ok) { setState('none'); say(T.transcriptFailed); chips([{ label: T.download, action: { kind: 'download' } }].concat(baseChips())); return; }
        lastTranscriptAt = now;
        setState('found');
        say(fill(T.transcriptSent, { email: email }));
        chips(baseChips());
      });
    }

    /* ---- cart -----------------------------------------------------------
       Shopify's own public cart endpoints. Nothing here is ever reported as
       done until the response body confirms it. */

    function postJSON(url, body) {
      return fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (r) {
        return r.json().then(function (j) { return { ok: r.ok, status: r.status, body: j }; },
                             function () { return { ok: false, status: r.status, body: null }; });
      }).catch(function () { return { ok: false, status: 0, body: null }; });
    }

    function getCart() {
      return fetch(URLS.cart, { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.json(); })
        .catch(function () { return null; });
    }

    function cartChips(cart) {
      var c = [{ label: T.viewCart, href: URLS.cartPage }];
      if (!cart || cart.item_count > 0) c.push({ label: T.checkout, action: { kind: 'checkout' } });
      c.push({ label: T.keepShopping, action: { kind: 'discover' } });
      if (S.saved.length) c.push({ label: T.myPicks, action: { kind: 'picks' } });
      return c;
    }

    function renderCart(cart) {
      var box = el('div', 'specgt-sa__summary');
      cart.items.forEach(function (it) {
        var r = el('div', 'specgt-sa__cartRow');
        var title = lineTitle(it);
        var variant = lineVariant(it);

        r.appendChild(mediaNode(it.image, title));

        var b = el('div', 'specgt-sa__body');
        var a = el('a', 'specgt-sa__name', title);
        a.href = it.url;
        b.appendChild(a);
        if (variant) {
          b.appendChild(el('span', 'specgt-sa__meta', variant));
        }
        b.appendChild(el('span', 'specgt-sa__price', cents(it.line_price)));

        var qty = el('div', 'specgt-sa__qty');
        var mk = function (lbl, next, aria) {
          var btn = el('button', 'specgt-sa__qtyBtn', lbl);
          btn.type = 'button';
          btn.setAttribute('aria-label', aria);
          btn.addEventListener('click', function () { dispatch({ kind: 'line', key: it.key, quantity: next }); });
          return btn;
        };
        qty.appendChild(mk('−', it.quantity - 1, 'Decrease quantity'));
        qty.appendChild(el('span', 'specgt-sa__qtyNum', String(it.quantity)));
        qty.appendChild(mk('+', it.quantity + 1, 'Increase quantity'));
        var rm = el('button', 'specgt-sa__sumRemove', T.remove);
        rm.type = 'button';
        rm.addEventListener('click', function () { dispatch({ kind: 'line', key: it.key, quantity: 0 }); });
        qty.appendChild(rm);
        b.appendChild(qty);

        r.appendChild(b);
        box.appendChild(r);
      });
      log.appendChild(box);
      /* Recorded as static text, never as live rows: a replayed quantity
         button would be acting on line keys from a cart that has since
         changed. The history is honest, but it is history. */
      record({
        k: 'note',
        lines: cart.items.map(function (it) {
          var v = lineVariant(it);
          return lineTitle(it) + (v ? ' — ' + v : '') + ' × ' + it.quantity + ' — ' + cents(it.line_price);
        })
      });
      say(fill(T.cartTotal, { price: cents(cart.total_price) }));
      scrollDown();
    }

    function showCart(introOverride) {
      getCart().then(function (cart) {
        if (!cart) { setState('none'); say(T.cartUnavailable); chips([{ label: T.viewCart, href: URLS.cartPage }].concat(baseChips())); return; }
        if (!cart.item_count) { setState('idle'); say(T.cartEmpty); chips(baseChips()); return; }
        setState('found');
        say(introOverride || T.cartIntro);
        renderCart(cart);
        var c = cartChips(cart);
        if (cfg.affiliate && cfg.affiliate.enabled) {
          var existing = cart.attributes ? cart.attributes[cfg.affiliate.attribute] : null;
          if (!existing) c.push({ label: T.affiliateAdd, action: { kind: 'affiliate' } });
        }
        chips(c);
      });
    }

    function changeLine(key, quantity) {
      postJSON(URLS.change, { id: key, quantity: quantity }).then(function (res) {
        if (!res.ok || !res.body || !res.body.items) { setState('none'); say(T.changeFailed); chips(cartChips()); return; }
        if (quantity === 0) say(T.removed);
        if (!res.body.item_count) { setState('idle'); say(T.cartEmpty); chips(baseChips()); return; }
        setState('found');
        renderCart(res.body);
        chips(cartChips(res.body));
      });
    }

    /* ---- buying flow -----------------------------------------------------
       Variant selection is never skipped. Options are asked in the product's
       own order, only where there is a genuine choice, and only values that
       still resolve to an available variant are offered. */

    function beginBuy(p) {
      if (!p.available) { setState('none'); say(T.soldOut); chips(productChips(p)); return; }
      S.activeProduct = p.handle;
      S.buy = { handle: p.handle, chosen: {} };
      nextOption(p);
    }

    function matchesChosen(v, chosen, extraIdx, extraVal) {
      for (var k in chosen) {
        if (v.opts[k] !== chosen[k]) return false;
      }
      if (extraIdx != null && v.opts[extraIdx] !== extraVal) return false;
      return true;
    }

    function availableValues(p, idx) {
      var out = [];
      p.options[idx].values.forEach(function (val) {
        var ok = p.variants.some(function (v) {
          return v.available && matchesChosen(v, S.buy.chosen, idx, val);
        });
        if (ok) out.push(val);
      });
      return out;
    }

    function nextOption(p) {
      for (var i = 0; i < p.options.length; i++) {
        if (p.options[i].values.length > 1 && S.buy.chosen[i] == null) {
          var vals = availableValues(p, i);
          if (!vals.length) { setState('none'); say(T.noCombo); S.buy = null; chips(productChips(p)); return; }
          S.pending = { kind: 'option', idx: i };
          setState('idle');
          say(fill(T.whichOption, { option: p.options[i].name.toLowerCase() }));
          var c = vals.map(function (v) {
            return { label: v, action: { kind: 'option', idx: i, value: v } };
          });
          if (p.options[i].name.toLowerCase() === 'size' && cfg.sizeGuideUrl && p.type.toLowerCase() === 't-shirt') {
            c.push({ label: T.sizeGuide, href: cfg.sizeGuideUrl, blank: true });
          }
          chips(c);
          return;
        }
      }
      confirmVariant(p);
    }

    function resolveVariant(p) {
      var want = p.options.map(function (o, i) {
        return S.buy.chosen[i] != null ? S.buy.chosen[i] : o.values[0];
      });
      return p.variants.filter(function (v) {
        for (var i = 0; i < want.length; i++) if (v.opts[i] !== want[i]) return false;
        return true;
      })[0] || null;
    }

    function variantLabel(v) {
      return (v.title && v.title !== 'Default Title') ? v.title : '';
    }

    function confirmVariant(p) {
      var v = resolveVariant(p);
      if (!v || !v.available) { setState('none'); say(T.noCombo); S.buy = null; chips(productChips(p)); return; }
      S.pending = null;
      setState('found');
      say(fill(T.confirmAdd, {
        title: p.title,
        variant: variantLabel(v) || p.title,
        price: money(v.price)
      }));
      chips([
        { label: T.addToCart, primary: true, action: { kind: 'add', id: v.id, handle: p.handle } },
        { label: T.viewProduct, href: p.url },
        { label: T.startOver, action: { kind: 'reset' } }
      ]);
    }

    /* Nothing is called "added" until Shopify's own response says it was. */
    function addToCart(variantId, handle) {
      var p = byHandle(handle);
      setState('thinking');
      postJSON(URLS.add, { items: [{ id: variantId, quantity: 1 }] }).then(function (res) {
        var line = null;
        if (res.ok && res.body) {
          var items = res.body.items || (res.body.id ? [res.body] : []);
          line = items.filter(function (x) { return String(x.id) === String(variantId); })[0] || items[0] || null;
        }
        if (!line) {
          setState('none');
          var reason = (res.body && (res.body.description || res.body.message)) ? decodeEntities(res.body.description || res.body.message) : '';
          say(T.addFailed + (reason ? '\n' + reason : ''));
          chips([
            { label: T.retry, action: { kind: 'add', id: variantId, handle: handle } },
            p ? { label: T.viewProduct, href: p.url } : null,
            { label: T.keepShopping, action: { kind: 'discover' } }
          ]);
          return;
        }

        S.buy = null;
        setState('found');
        var vt = lineVariant(line);
        say(vt
          ? fill(T.added, { title: lineTitle(line), variant: vt })
          : fill(T.addedSimple, { title: lineTitle(line) }));

        /* One useful next step, not three sales pitches. */
        say(T.crossSell);
        var c = [{ label: T.yesPlease, action: { kind: 'pairs', handle: handle } },
                 { label: T.viewCart, href: URLS.cartPage },
                 { label: T.checkout, action: { kind: 'checkout' } },
                 { label: T.keepShopping, action: { kind: 'discover' } }];
        chips(c);
      });
    }

    /* ---- checkout guidance -----------------------------------------------
       The assistant never imitates checkout and never touches payment data.
       It confirms there is something to buy and hands off to Shopify. */

    function checkout() {
      getCart().then(function (cart) {
        if (!cart) { setState('none'); say(T.cartUnavailable); chips([{ label: T.viewCart, href: URLS.cartPage }].concat(baseChips())); return; }
        if (!cart.item_count) { setState('idle'); say(T.cartEmpty); chips(baseChips()); return; }
        setState('found');
        say(fill(T.checkoutReady, {
          n: cart.item_count + (cart.item_count === 1 ? ' item' : ' items'),
          price: cents(cart.total_price)
        }));
        var c = [{ label: T.goCheckout, href: URLS.checkout, primary: true },
                 { label: T.viewCart, href: URLS.cartPage }];
        if (cfg.affiliate && cfg.affiliate.enabled) {
          var existing = cart.attributes ? cart.attributes[cfg.affiliate.attribute] : null;
          c.push({ label: existing ? T.affiliateReplace : T.affiliateAdd, action: { kind: 'affiliate' } });
        }
        c.push({ label: T.keepShopping, action: { kind: 'discover' } });
        chips(c);
      });
    }

    /* ---- affiliate / referral code ---------------------------------------
       Uses the mechanism the store already has: a cart attribute written
       through /cart/update.js, mirrored into the same localStorage key the
       cart page reads so the field there stays in sync. Optional, never a
       blocker, and only confirmed once the response echoes it back. */

    function affiliate() {
      if (!cfg.affiliate || !cfg.affiliate.enabled) {
        setState('idle'); say(T.affiliateNone); chips(baseChips()); return;
      }
      getCart().then(function (cart) {
        var attr = cfg.affiliate.attribute;
        var existing = (cart && cart.attributes) ? cart.attributes[attr] : null;
        setState('idle');
        if (existing) {
          say(fill(T.affiliateHas, { code: decodeEntities(existing) }));
          chips([{ label: T.affiliateReplace, action: { kind: 'affiliateEnter' } },
                 { label: T.checkout, action: { kind: 'checkout' } },
                 { label: T.keepShopping, action: { kind: 'discover' } }]);
          return;
        }
        say(T.affiliateAsk);
        chips([{ label: T.affiliateEnter, action: { kind: 'affiliateEnter' } },
               { label: T.affiliateSkip, action: { kind: 'checkout' } }]);
      });
    }

    function applyAffiliate(code) {
      var attr = cfg.affiliate.attribute;
      var payload = { attributes: {} };
      payload.attributes[attr] = code;
      setState('thinking');
      postJSON(URLS.update, payload).then(function (res) {
        var got = (res.ok && res.body && res.body.attributes) ? res.body.attributes[attr] : null;
        if (got !== code) {
          setState('none');
          say(T.affiliateFailed);
          chips([{ label: T.retry, action: { kind: 'affiliateEnter' } },
                 { label: T.viewCart, href: URLS.cartPage },
                 { label: T.checkout, action: { kind: 'checkout' } }]);
          return;
        }
        /* keep the cart page's own field in step */
        try { window.localStorage.setItem(AFFILIATE_LS, code); } catch (e) {}
        setState('found');
        say(fill(T.affiliateSet, { code: code }));
        chips([{ label: T.goCheckout, href: URLS.checkout, primary: true },
               { label: T.viewCart, href: URLS.cartPage },
               { label: T.keepShopping, action: { kind: 'discover' } }]);
      });
    }

    /* ---- "help me order" -------------------------------------------------
       Inspects real context and never dead-ends. */

    function helpOrder() {
      getCart().then(function (cart) {
        if (!cart) { setState('none'); say(T.cartUnavailable); chips([{ label: T.viewCart, href: URLS.cartPage }].concat(baseChips())); return; }
        if (!cart.item_count) {
          setState('idle');
          say(T.cartEmpty);
          if (S.saved.length) {
            chips([{ label: T.myPicks, action: { kind: 'picks' } }].concat(baseChips()));
          } else {
            chips(baseChips());
          }
          return;
        }
        showCart();
      });
    }

    /* ---- parse free text into a structured action ----------------------- */

    function parse(q) {
      var low = q.toLowerCase();

      /* Anything that genuinely needs a person is routed out before the
         shopping intents, so a complaint or an order problem is never
         answered with a product recommendation. */
      if (/\b(speak|talk|chat) (to|with) (a |an )?(human|person|someone|agent|rep|representative|staff|team)\b/.test(low) ||
          /\b(human|real person|customer (service|support)|contact (you|support|someone)|complain|complaint)\b/.test(low) ||
          /\b(where is my order|track my order|my order (is|has|never)|damaged|defective|wrong item|missing|refund my|cancel my order)\b/.test(low))
        return { kind: 'human' };

      if (/\b(transcript|copy of (this|our) (chat|conversation)|email (me )?(this|the) (chat|conversation)|save (this|our) (chat|conversation))\b/.test(low))
        return { kind: 'transcript' };
      if (/\b(download (the )?(chat|conversation|transcript))\b/.test(low)) return { kind: 'download' };
      if (/\b(new conversation|start over completely|reset (the )?chat|clear (the )?(chat|conversation))\b/.test(low))
        return { kind: 'clearAsk' };

      /* Referral codes are a real mechanism here, so they are matched before
         the generic discount answer, which is a refusal. */
      if (/\b(affiliate|referral)\b/.test(low)) return { kind: 'affiliate' };

      if (/\b(my cart|in my cart|cart total|the cart|basket)\b/.test(low) || /\bwhat.*\bcart\b/.test(low))
        return { kind: 'cart' };
      if (/\bremove\b/.test(low)) return { kind: 'cart' };
      if (/\b(check ?out|ready to (order|buy|pay)|where do i pay|complete my (purchase|order)|place my order|pay now)\b/.test(low))
        return { kind: 'checkout' };
      if (/\b(help me order|help me buy|how do i order|how do i buy)\b/.test(low))
        return { kind: 'order' };
      if (/\b(add (it|this|that|one)? ?to (my )?cart|i want (it|this|one|that)|i'?ll take (it|this|one)|buy (it|this)|order (it|this))\b/.test(low))
        return { kind: 'buy' };
      if (/\b(save (it|this|that)|add to (my )?picks)\b/.test(low)) return { kind: 'save' };
      if (/\b(my picks|saved|shopping list)\b/.test(low)) return { kind: 'picks' };

      /* Policy. Production is kept strictly separate from delivery: production
         time is approved, transit time is not. Delivery is matched first so it
         cannot fall through to the production answer. */
      if (/\b(deliver|delivery|arrive|arrival|receive|get here|how many days|transit|shipping take|after it ships)\b/.test(low))
        return { kind: 'policy', topic: 'delivery' };
      if (/\b(production|how long.*(make|made|produce)|made to order|manufactur)\b/.test(low))
        return { kind: 'policy', topic: 'production' };
      if (/\b(ship|shipping|postage|international|customs)\b/.test(low))
        return { kind: 'policy', topic: 'delivery' };
      if (/\b(return|refund|exchange|send.*back|wrong size)\b/.test(low))
        return { kind: 'policy', topic: 'returns' };
      if (/\b(discount|coupon|promo|voucher|sale)\b/.test(low))
        return { kind: 'policy', topic: 'discount' };

      var refers = /\b(this|it|that|these|they|them)\b/.test(low);

      if (/\bsizes?\b|\bfit\b/.test(low)) return { kind: 'ask', which: 'sizes', refers: refers };
      if (/\bcolou?rs?\b/.test(low)) return { kind: 'ask', which: 'colours', refers: refers };
      if (/\b(how much|price|cost)\b/.test(low)) return { kind: 'ask', which: 'price', refers: refers };
      if (/\b(available|in stock|stock)\b/.test(low)) return { kind: 'ask', which: 'available', refers: refers };
      if (/\b(similar|like this|something else like|more like)\b/.test(low)) return { kind: 'similar' };
      if (/\b(go with|goes with|pair|pairs|match|matches|along with|complement)\b/.test(low)) return { kind: 'pairs' };
      if (/\b(cheaper|less|lower|budget)\b/.test(low)) return { kind: 'cheaper' };
      if (/\b(more|another|else|next)\b/.test(low)) return { kind: 'more' };
      if (/\bcollections?\b|\bbrowse\b/.test(low)) return { kind: 'collections' };
      if (/\bstart over|reset\b/.test(low)) return { kind: 'reset' };

      var ceiling = null;
      var m = low.match(/(?:under|below|less than|max|up to|cheaper than)\s*\$?\s*(\d+(?:\.\d+)?)/);
      if (m) ceiling = parseFloat(m[1]);

      var type = null;
      TYPES.forEach(function (t) { if (!type && t.re.test(low)) type = t; });

      if (/\bgift\b|\bpresent\b/.test(low)) {
        var qual = /racing|motorsport|track/.test(low) ? 'racing'
                 : (/car culture|culture|cars?\b|automotive|jdm|drift/.test(low) ? 'car-culture' : null);
        if (qual || type || ceiling != null) {
          return { kind: 'gift', qualifier: qual, types: type ? type.types : null, ceiling: ceiling };
        }
        return { kind: 'gift' };
      }

      if (type || ceiling != null) {
        /* Naming a specific product should surface that product, not a
           generic list of its category. Anything left over once the category
           words and the price phrasing are removed is kept as a search
           within the category. Digits are only dropped when they were the
           price we just parsed, so a model number like 911 survives. */
        var leftover = words(low).filter(function (w) {
          if (type && type.re.test(w)) return false;
          if (PRICE_WORDS.indexOf(w) !== -1) return false;
          if (ceiling != null && /^\d+$/.test(w)) return false;
          return true;
        });
        return {
          kind: 'filter',
          types: type ? type.types : null,
          ceiling: ceiling,
          q: leftover.length ? leftover.join(' ') : ''
        };
      }

      return { kind: 'text', q: q };
    }

    /* ---- journey classification -----------------------------------------
       A new top-level journey resets transient discovery filters; a
       refinement keeps them. This is what stops an old "under $30" ceiling
       silently surviving into an unrelated later search. */

    var NEW_JOURNEY = ['type', 'tag', 'price', 'filter', 'collections', 'discover', 'budget', 'reset', 'text'];

    function isNewJourney(a) {
      if (a.kind === 'gift') return !(S.pending && S.pending.kind === 'gift');
      return NEW_JOURNEY.indexOf(a.kind) !== -1;
    }

    /* ---- dispatch -------------------------------------------------------- */

    /* Cart and policy work without the catalogue; everything else needs it. */
    var NO_CATALOGUE = ['policy', 'cart', 'checkout', 'order', 'line', 'affiliate', 'affiliateEnter', 'affiliateCode', 'copy', 'email',
                        'newConvo', 'clearAsk', 'clearYes', 'transcript', 'download', 'transcriptCancel', 'human', 'copySummary'];

    function dispatch(a) {
      if (!a) return;
      if (NO_CATALOGUE.indexOf(a.kind) !== -1) { run(a); return; }
      load().then(function (ok) {
        if (!ok) { setState('none'); say(T.offline); chips(baseChips()); return; }
        run(a);
      });
    }

    function run(a) {
      /* 1. pending follow-up state wins over everything */
      if (S.pending && a.kind === 'text') {
        if (S.pending.kind === 'gift') {
          var low = a.q.toLowerCase();
          var qual = /racing|motorsport|track/.test(low) ? 'racing'
                   : (/culture|cars?\b|automotive|jdm|drift/.test(low) ? 'car-culture'
                   : (/general|anything|not sure|unsure/.test(low) ? 'general' : null));
          if (qual) a = { kind: 'gift', qualifier: qual };
        } else if (S.pending.kind === 'affiliate') {
          var code = a.q.trim();
          S.pending = null;
          if (!code) { say(T.affiliateFailed); chips(baseChips()); return; }
          applyAffiliate(code);
          return;
        } else if (S.pending.kind === 'email') {
          var addr = a.q.trim();
          if (!validEmail(addr)) {
            setState('none');
            say(T.transcriptBadEmail);
            chips([{ label: T.download, action: { kind: 'download' } },
                   { label: T.transcriptCancel, action: { kind: 'transcriptCancel' } }]);
            return;   /* pending stays set, so the next line is another attempt */
          }
          S.pending = null;
          setState('idle');
          say(fill(T.transcriptConfirm, { email: addr }));
          chips([{ label: T.transcriptSend, primary: true, action: { kind: 'transcriptSend', email: addr } },
                 { label: T.transcriptCancel, action: { kind: 'transcriptCancel' } }]);
          return;
        }
      }

      if (isNewJourney(a)) startJourney();

      switch (a.kind) {

        /* ---- product focus ---- */

        case 'pick': {
          var then = (S.pending && S.pending.kind === 'pick') ? S.pending.then : null;
          S.pending = null;
          var p = byHandle(a.handle);
          if (!p) return;
          if ((a.then || then) === 'similar') { similar(p); return; }
          if ((a.then || then) === 'pairs') { pairsWith(p); return; }
          detail(p, a.then || then || 'detail');
          return;
        }

        case 'detail': {
          var pd = byHandle(a.handle);
          if (pd) detail(pd, 'detail');
          return;
        }

        /* 3/4. "this" resolves to the PDP product, then the active product,
           then a single recommendation. Several candidates -> ask, never guess. */
        case 'ask': {
          var s = subject();
          if (!s) {
            if (S.results.length > 1) { askWhich(a.which); return; }
            setState('idle');
            say(a.which === 'sizes' ? T.sizingFallback : T.noSubject);
            chips(baseChips());
            return;
          }
          detail(s, a.which);
          return;
        }

        case 'similar': {
          var sp = byHandle(a.handle) || subject();
          if (!sp) { if (S.results.length > 1) { askWhich('similar'); return; } say(T.noSubject); chips(baseChips()); return; }
          similar(sp);
          return;
        }

        case 'pairs': {
          var pw = byHandle(a.handle) || subject();
          if (!pw) { if (S.results.length > 1) { askWhich('pairs'); return; } say(T.noSubject); chips(baseChips()); return; }
          pairsWith(pw);
          return;
        }

        case 'save': {
          var sv = byHandle(a.handle) || subject();
          if (!sv) { say(T.noSubject); chips(baseChips()); return; }
          toggleSave(sv.handle);
          chips(productChips(sv));
          return;
        }

        /* ---- buying ---- */

        case 'buy': {
          var bp = byHandle(a.handle) || subject();
          if (!bp) {
            if (S.results.length > 1) { askWhich('buy'); return; }
            say(T.noSubject); chips(baseChips()); return;
          }
          beginBuy(bp);
          return;
        }

        case 'option': {
          if (!S.buy) return;
          var op = byHandle(S.buy.handle);
          if (!op) { S.buy = null; return; }
          S.buy.chosen[a.idx] = a.value;
          S.pending = null;
          nextOption(op);
          return;
        }

        case 'add': { addToCart(a.id, a.handle); return; }

        /* ---- cart, checkout, affiliate ---- */

        case 'cart': { setState('thinking'); showCart(); return; }
        case 'line': { changeLine(a.key, a.quantity); return; }
        case 'order': { setState('thinking'); helpOrder(); return; }
        case 'checkout': { setState('thinking'); checkout(); return; }
        case 'affiliate': { affiliate(); return; }
        case 'affiliateEnter': {
          S.pending = { kind: 'affiliate' };
          setState('idle');
          say(T.affiliatePrompt);
          return;
        }

        /* ---- policy ---- */

        case 'policy': {
          setState('idle');
          say((cfg.policy && cfg.policy[a.topic]) || T.none);
          chips(baseChips());
          return;
        }

        /* ---- refinement: every established filter survives ---- */

        case 'cheaper': {
          var cur = S.filters.ceiling;
          if (cur == null) {
            cur = S.results.reduce(function (m, x) { return x.p.price ? Math.max(m, x.p.price.min) : m; }, 0) || 40;
          }
          var steps = [40, 30, 25, 20, 15, 10];
          var next = null;
          for (var i = 0; i < steps.length; i++) if (steps[i] < cur) { next = steps[i]; break; }
          if (next == null) { var lo = lowest(); next = lo == null ? 10 : Math.max(lo, 5); }
          S.filters.ceiling = next;
          runFilters(null, fill(T.noneCheaper, { price: money(next) }));
          return;
        }

        case 'more': {
          /* After a restore the rendered conversation is back but the result
             set behind it is not, so it is rebuilt from the stored filters
             rather than telling the shopper there is nothing more. */
          if (!S.results.length && (S.filters.types || S.filters.tag || S.filters.ceiling != null || S.filters.q)) {
            S.results = query(S.filters.q || '', {
              types: S.filters.types, tag: S.filters.tag, ceiling: S.filters.ceiling
            });
            S.shownCount = Math.min(S.shownCount, S.results.length);
          }
          if (!S.results.length) { say(T.noMore); chips(relaxChips()); return; }
          showMore();
          return;
        }

        case 'relax': {
          if (a.what === 'ceiling') S.filters.ceiling = null;
          else S.filters = { types: null, tag: null, ceiling: null, gift: false, q: '' };
          runFilters();
          return;
        }

        /* ---- discovery (all of these start a fresh journey) ---- */

        case 'discover': {
          setState('idle');
          say(T.discoverPrompt);
          chips([
            { label: T.culture, action: { kind: 'tag', tag: 'car culture' } },
            { label: T.racing, action: { kind: 'tag', tag: 'racing' } },
            { label: T.chipTee, action: { kind: 'type', types: ['t-shirt'] } },
            { label: T.chipBudget, action: { kind: 'budget' } },
            { label: T.chipCollections, action: { kind: 'collections' } }
          ]);
          return;
        }

        case 'budget': {
          setState('idle');
          say(T.budgetPrompt);
          var bands = (cfg.budgets && cfg.budgets.length ? cfg.budgets : [20, 30, 50])
            .filter(function (n) { return isFinite(n) && n > 0; });
          chips(bands.map(function (n) {
            return { label: 'Under ' + money(n), action: { kind: 'price', ceiling: n } };
          }));
          return;
        }

        case 'type': { S.filters.types = a.types; runFilters(); return; }
        case 'tag': { S.filters.tag = a.tag; runFilters(); return; }
        case 'price': { S.filters.ceiling = a.ceiling; runFilters(); return; }

        case 'filter': {
          if (a.types) S.filters.types = a.types;
          if (a.ceiling != null) S.filters.ceiling = a.ceiling;
          if (a.q) S.filters.q = a.q;
          runFilters(a.q ? T.matchesIntro : null);
          return;
        }

        case 'collections': {
          setState('idle');
          if (!cfg.collections || !cfg.collections.length) { say(T.none); chips(baseChips()); return; }
          say(T.collectionsIntro);
          var box = el('div', 'specgt-sa__cards');
          cfg.collections.forEach(function (c) {
            var a2 = el('a', 'specgt-sa__collection');
            a2.href = c.url;
            a2.appendChild(el('span', 'specgt-sa__name', c.title));
            a2.appendChild(el('span', 'specgt-sa__meta', c.count + (c.count === 1 ? ' product' : ' products')));
            box.appendChild(a2);
          });
          log.appendChild(box);
          chips(baseChips());
          scrollDown();
          return;
        }

        /* Gift: asking the qualifier sets pending state, so the answer to it
           is never re-read as a fresh top-level gift request. */
        case 'gift': {
          if (!a.qualifier && !a.types && a.ceiling == null) {
            S.pending = { kind: 'gift' };
            S.filters.gift = true;
            setState('idle');
            say(T.giftPrompt);
            chips([
              { label: T.giftCulture, action: { kind: 'gift', qualifier: 'car-culture' } },
              { label: T.giftRacing, action: { kind: 'gift', qualifier: 'racing' } },
              { label: T.giftGeneral, action: { kind: 'gift', qualifier: 'general' } }
            ]);
            return;
          }
          S.pending = null;
          S.filters.gift = true;
          if (a.types) S.filters.types = a.types;
          if (a.ceiling != null) S.filters.ceiling = a.ceiling;
          if (a.qualifier === 'car-culture') S.filters.tag = 'car culture';
          else if (a.qualifier === 'racing') S.filters.tag = 'racing';
          else S.filters.tag = null;
          runFilters();
          return;
        }

        case 'reset': {
          setState('idle');
          say(T.discoverPrompt);
          chips(baseChips());
          return;
        }

        /* ---- picks, clipboard ---- */

        case 'picks': { setState('idle'); picks(); return; }
        case 'copy': { copyPicks(a.text); return; }
        case 'email': { setState('idle'); say(T.emailUnavailable); chips(baseChips()); return; }

        /* ---- conversation controls and transcript ---- */

        case 'newConvo': { startConversation(T.newConvoDone); return; }

        case 'clearAsk': {
          setState('idle');
          say(T.clearAsk);
          chips([{ label: T.clearYes, action: { kind: 'clearYes' } },
                 { label: T.clearNo, action: { kind: 'discover' } }]);
          return;
        }

        case 'clearYes': { clearConversation(); return; }
        case 'transcript': { requestTranscript(); return; }
        case 'download': { downloadTranscript(); return; }

        case 'transcriptCancel': {
          S.pending = null;
          setState('idle');
          say(T.transcriptCancelled);
          chips(baseChips());
          return;
        }

        case 'transcriptSend': { S.pending = null; sendTranscript(a.email); return; }

        /* Some things need a person. Say so and point at the real channel
           rather than improvising an answer. */
        case 'human': {
          setState('idle');
          say(T.human + (cfg.supportHint ? '\n' + cfg.supportHint : ''));
          /* Stated every time, and not editable away: Inbox has no supported
             way to receive this conversation. Nothing is copied until the
             shopper asks for it. */
          say(T.humanNote);
          var hc = [{ label: T.copySummary, primary: true, action: { kind: 'copySummary' } }];
          if (cfg.supportUrl) hc.push({ label: T.contactUs, href: contactUrlWithSummary() });
          hc.push({ label: T.download, action: { kind: 'download' } });
          chips(hc.concat(baseChips()));
          return;
        }

        case 'copySummary': { copySummary(); return; }

        /* ---- keyword retrieval, then a fallback that never invents ---- */

        case 'text':
        default: {
          var scored = query(a.q, {});
          present(scored, T.matchesIntro, fill(T.noneFor, { q: a.q }));
          return;
        }
      }
    }

    /* ---- context --------------------------------------------------------- */

    /* If the shopper arrived here from an escalation link, put the summary
       in the contact form for them to review. */
    prefillContactForm();

    var path = window.location.pathname || '/';
    var pdp = path.match(/\/products\/([^/?#]+)/);
    if (pdp) S.currentPageProduct = pdp[1];
    var onCart = /\/cart(\/|$|\?)/.test(path);

    function openingChips() {
      if (S.currentPageProduct) {
        return [
          { label: T.seeSizes, action: { kind: 'ask', which: 'sizes', refers: true } },
          { label: T.addToCart, action: { kind: 'buy' } },
          { label: T.similar, action: { kind: 'similar' } },
          { label: T.chipOrder, action: { kind: 'order' } }
        ];
      }
      if (onCart) {
        return [
          { label: T.viewCart, action: { kind: 'cart' } },
          { label: T.pairs, action: { kind: 'order' } },
          { label: T.checkout, action: { kind: 'checkout' } },
          { label: T.chipDiscover, action: { kind: 'discover' } }
        ];
      }
      return baseChips();
    }

    function opening() {
      if (S.currentPageProduct) return T.welcomePdp;
      if (onCart) return T.welcomeCart;
      return T.welcome;
    }

    /* ---- open / close ---------------------------------------------------- */

    var lastFocus = null;

    function open() {
      lastFocus = document.activeElement;
      root.setAttribute('data-open', 'true');
      panel.setAttribute('aria-hidden', 'false');
      launcher.setAttribute('aria-expanded', 'true');
      if (!log.childElementCount) {
        var stored = readConvo();
        if (stored) {
          /* Restore, and do NOT repeat the welcome — the shopper is in the
             middle of something, not arriving. */
          convo = stored;
          var hasCards = stored.entries.some(function (e) { return e.k === 'cards'; });
          if (hasCards) { load().then(function () { replay(); }); }
          else { replay(); }
        } else {
          startConversation();
        }
      }
      if (prefilled) {
        prefilled = false;
        say(T.contactPrefilled);
      }
      window.setTimeout(function () { input.focus(); }, 60);
    }

    function close() {
      root.setAttribute('data-open', 'false');
      panel.setAttribute('aria-hidden', 'true');
      launcher.setAttribute('aria-expanded', 'false');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    launcher.addEventListener('click', open);
    closeBtn.addEventListener('click', close);

    /* ---- conversation options menu -------------------------------------- */

    var menuBtn = root.querySelector('[data-sa-menu-btn]');
    var menu = root.querySelector('[data-sa-menu]');

    function closeMenu() {
      if (!menu) return;
      menu.hidden = true;
      if (menuBtn) menuBtn.setAttribute('aria-expanded', 'false');
    }

    if (menuBtn && menu) {
      menuBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        var openNow = menu.hidden;
        menu.hidden = !openNow;
        menuBtn.setAttribute('aria-expanded', openNow ? 'true' : 'false');
      });

      Array.prototype.forEach.call(menu.querySelectorAll('[data-sa-action]'), function (b) {
        b.addEventListener('click', function () {
          var kind = b.getAttribute('data-sa-action');
          closeMenu();
          say(b.textContent, 'user');
          dispatch({ kind: kind });
        });
      });

      /* A click anywhere else, or Escape, puts it away. */
      document.addEventListener('click', function (e) {
        if (menu.hidden) return;
        if (menu.contains && menu.contains(e.target)) return;
        closeMenu();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (menu && !menu.hidden) { closeMenu(); if (menuBtn && menuBtn.focus) menuBtn.focus(); return; }
      if (root.getAttribute('data-open') === 'true') close();
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var v = input.value.trim();
      if (!v) return;
      input.value = '';
      say(v, 'user');
      setState('thinking');
      /* A pending code or email address is raw text, not an intent to be
         parsed — "sales@…" must not be read as a request about a sale. */
      if (S.pending && (S.pending.kind === 'affiliate' || S.pending.kind === 'email')) {
        run({ kind: 'text', q: v });
        return;
      }
      dispatch(parse(v));
    });

    /* The newsletter popup is fixed full-width along the bottom, so the
       launcher steps aside while it is open. */
    var popup = document.querySelector('.NewsletterPopup');
    if (popup && window.MutationObserver) {
      var sync = function () {
        root.setAttribute('data-popup', popup.getAttribute('aria-hidden') === 'false' ? 'true' : 'false');
      };
      sync();
      new MutationObserver(sync).observe(popup, { attributes: true, attributeFilter: ['aria-hidden'] });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
